import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, FileText, Clock } from "lucide-react";

export default function LibraryPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/jobs").then((r) => setJobs(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this catalog?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const cleanup = async () => {
    try {
      const { data } = await api.post("/jobs/cleanup");
      toast.success(`Cleaned up ${data.removed} expired catalog(s)`);
      refresh();
    } catch (e) { toast.error("Cleanup failed"); }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const d = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(d / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8" data-testid="library-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <div className="overline">History</div>
          <h1 className="font-serif-editorial text-4xl mt-2">Generated Catalogs</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Generated files expire after the retention window and are cleaned up automatically.
          </p>
        </div>
        <button
          data-testid="cleanup-expired-btn"
          onClick={cleanup}
          className="text-xs uppercase tracking-widest px-4 py-2.5 border hairline rounded-sm bg-white hover:bg-neutral-50"
        >
          Clean Up Expired
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-neutral-500">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="py-16 text-center border hairline bg-white rounded-sm">
          <FileText className="w-6 h-6 mx-auto text-neutral-400 mb-3" />
          <p className="text-sm text-neutral-500">No catalogs generated yet.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {jobs.map((j) => {
            const left = daysLeft(j.expires_at);
            return (
              <div
                key={j.id}
                data-testid={`job-row-${j.id}`}
                className="paper rounded-sm p-4 sm:p-5 flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-5"
              >
                <div className="w-10 h-12 grid place-items-center border hairline rounded-sm bg-neutral-100">
                  <FileText className="w-4 h-4 text-neutral-500" />
                </div>
                <div className="flex-1 min-w-[60%] sm:min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-serif-editorial text-lg sm:text-xl truncate">{j.filename}</div>
                    {j.is_bundled_sample && (
                      <span
                        data-testid={`sample-badge-${j.id}`}
                        className="text-[9px] uppercase tracking-[0.24em] px-1.5 py-0.5 rounded-sm bg-[var(--brand-accent)]/12 border border-[var(--brand-accent)]/40 text-[color:var(--brand-accent)] font-medium"
                        style={{ backgroundColor: "#FBF3DC", borderColor: "#E5CE87", color: "#8A6D14" }}
                        title="Generated from the bundled sample catalog"
                      >
                        Sample
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">
                    {j.item_count} items · {j.branding?.page_size} · {new Date(j.created_at).toLocaleString()}
                  </div>
                </div>
                <div
                  className={`px-3 py-1 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5 ${
                    j.is_expired
                      ? "bg-[var(--brand-warn-bg)] text-[var(--brand-warn-text)] border border-[var(--brand-warn-border)]"
                      : "border hairline bg-white text-neutral-600"
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {j.is_expired ? "Expired" : `${left}d left`}
                </div>
                {!j.is_expired && (
                  <a
                    data-testid={`download-job-${j.id}`}
                    href={fileUrl(j.pdf_file_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-neutral-900 rounded-sm hover:bg-neutral-900 hover:text-white transition-colors"
                  >
                    <Download className="w-3 h-3" /> Download
                  </a>
                )}
                <button
                  data-testid={`delete-job-${j.id}`}
                  onClick={() => remove(j.id)}
                  className="p-2 border hairline rounded-sm hover:bg-neutral-50 hover:text-red-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
